import { NOTIFICATION_TYPES } from '../redux/notification/notification.types';

/**
 * Quais lembretes da agenda merecem um aviso na tela AGORA.
 *
 * ## O que faltava
 *
 * O lembrete do compromisso já existia e já chegava: `agenda:dispatch-reminders`
 * roda a cada minuto e cria uma notificação `calendar_reminder`. Só que ela
 * aparecia **apenas dentro do sininho** -- quem não abrisse o sininho não ficava
 * sabendo do compromisso, que é justamente o ponto de ter lembrete.
 *
 * Pedido de 14/10/2026: no horário programado, abrir um aviso que **não some
 * sozinho** -- só no X ou no botão de ver o evento.
 *
 * ## Por que a regra mora aqui, e não dentro do sininho
 *
 * Porque ela é decidível sem React: entram a lista de notificações, os ids já
 * avisados e a hora; sai a lista de quem avisar. Dentro do componente, só dava
 * para exercitá-la renderizando o sininho inteiro e esperando um `setInterval`.
 */

/**
 * Quanto tempo depois de criado um lembrete ainda vale a pena interromper.
 *
 * O sininho busca a cada minuto, então um lembrete recém-criado sempre cai
 * dentro desta janela. A janela existe para o caso contrário: quem volta de
 * férias e abre o sistema receberia uma pilha de avisos presos na tela, um
 * para cada compromisso da semana passada, cada um exigindo um clique. O
 * lembrete antigo continua no sininho -- ele só deixa de interromper.
 */
export const JANELA_DO_AVISO_MINUTOS = 60;

const tipoDa = (notificacao) => notificacao?.data?.type || notificacao?.type;

const ehLembreteDaAgenda = (notificacao) => tipoDa(notificacao) === NOTIFICATION_TYPES.CALENDAR_REMINDER;

const ehChatEncerradoPeloCliente = (notificacao) => (
    tipoDa(notificacao) === NOTIFICATION_TYPES.CHAT_CLOSED_BY_CLIENT
);

/**
 * Minutos desde que o lembrete foi criado, ou `null` se a data não presta.
 *
 * `null` -- e não zero -- quando a data é inválida: tratar data quebrada como
 * "agora" faria um registro corrompido interromper o operador para sempre.
 */
const minutosDesde = (quando, agora) => {
    const criado = new Date(quando);

    if (Number.isNaN(criado.getTime())) return null;

    return (agora.getTime() - criado.getTime()) / 60000;
};

/**
 * Os lembretes a avisar.
 *
 * @param {Array} notificacoes  o que o sininho carregou
 * @param {Set|Array} jaAvisados ids que já viraram aviso (inclusive em outra
 *        sessão do navegador -- ver `avisosJaMostrados`)
 * @param {Date} [agora]
 * @returns {Array} as notificações a avisar, da mais antiga para a mais nova
 */
const paraAvisar = (aceita) => (notificacoes, jaAvisados, agora = new Date()) => {
    if (!Array.isArray(notificacoes)) return [];

    // Normalizado para TEXTO dos dois lados. A api manda `id` numérico e o
    // `localStorage` guarda texto; comparar sem converter faz o `has` falhar
    // em silêncio, e o mesmo lembrete voltaria a avisar a cada busca.
    const vistos = new Set([...(jaAvisados || [])].map(String));

    return notificacoes
        .filter((n) => {
            if (!n || n.id === undefined || n.id === null) return false;
            if (!aceita(n)) return false;

            // Já lido significa que o operador tomou conhecimento por outro
            // caminho (abriu o sininho, clicou). Interromper depois disso é
            // repetir o que ele já viu.
            if (n.is_read) return false;

            if (vistos.has(String(n.id))) return false;

            const minutos = minutosDesde(n.created_at, agora);

            if (minutos === null) return false;

            // O futuro entra: o relógio do servidor e o do navegador não são o
            // mesmo, e recusar por alguns segundos de diferença perderia
            // justamente o lembrete recém-criado, que é o que importa.
            return minutos <= JANELA_DO_AVISO_MINUTOS;
        })
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
};

/** Os lembretes de compromisso que ainda devem virar aviso na tela. */
export const lembretesParaAvisar = paraAvisar(ehLembreteDaAgenda);

/**
 * Os avisos de chat encerrado PELO CLIENTE que ainda devem virar toast.
 *
 * Mesma regra dos lembretes -- nao lido, nao avisado antes, recente --, e por
 * isso sai da mesma fabrica: duplicar a regra significaria divergir dela no
 * dia em que uma das duas mudasse.
 *
 * Pedido dele em 15/10/2026: em vez de levar o operador ao ticket à força, um
 * aviso preso no canto com um botão para ir. Quem decide aonde ir continua
 * sendo `destinoDaNotificacao`, o mesmo caminho do sininho -- dois caminhos
 * para "abrir o ticket" divergiriam no dia em que a rota mudasse.
 */
export const chatsEncerradosParaAvisar = paraAvisar(ehChatEncerradoPeloCliente);

const CHAVE = 'alodesk:lembretes-avisados';

/** Quantos ids guardar. O suficiente para meses de uso sem crescer sem fim. */
const TETO_DE_IDS = 200;

/**
 * Os ids que já viraram aviso, lidos do `localStorage`.
 *
 * Persistido, e não guardado só em memória, porque o aviso não pode voltar a
 * cada F5: o lembrete continua NÃO LIDO no sininho depois de o operador fechar
 * o aviso no X -- e é assim que tem de ser, fechar o aviso não é dizer que
 * tratou do compromisso. Sem persistir, qualquer recarga traria tudo de volta.
 *
 * Nunca estoura: `localStorage` pode estar indisponível (aba anônima, cota,
 * site bloqueado) e isso não pode derrubar o sininho. Na dúvida, devolve vazio
 * -- no pior caso o aviso aparece de novo, que é melhor que a tela quebrar.
 */
export const avisosJaMostrados = () => {
    try {
        const cru = window.localStorage.getItem(CHAVE);
        const lido = cru ? JSON.parse(cru) : [];

        return new Set(Array.isArray(lido) ? lido.map(String) : []);
    } catch (e) {
        return new Set();
    }
};

/** Acrescenta ids à lista de já avisados, mantendo só os mais recentes. */
export const anotarAvisosMostrados = (ids) => {
    try {
        const todos = [...avisosJaMostrados(), ...(ids || []).map(String)];
        const recortado = todos.slice(-TETO_DE_IDS);

        window.localStorage.setItem(CHAVE, JSON.stringify(recortado));
    } catch (e) {
        // Sem persistência o aviso pode repetir numa recarga. É o custo
        // aceitável; derrubar a tela não é.
    }
};
